import { Button, ChoiceChips, Dialog } from '@platform/design-system';
import type { TypedClient } from '@platform/openapi';
import { useNavigate } from '@tanstack/react-router';
import { CheckCheck } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { usePageContext } from '../usePageContext.ts';
import { NotificationList, useChooseNotification } from './NotificationList.tsx';
import styles from './Notifications.module.css';
import {
  NOTIFICATION_FILTERS,
  PANEL_PAGE_SIZE,
  useMarkRead,
  useMarkSeen,
  useNotificationPages,
  type InboxCounts,
  type NotificationFilter,
} from './useNotifications.ts';

export type NotificationsPanelProps = { api: TypedClient; counts: InboxCounts; onClose: () => void };

const useSeeOnOpen = (api: TypedClient, newestId: string | undefined, unseen: number) => {
  const { mutate: markSeen } = useMarkSeen(api);
  const seenUpTo = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (newestId === undefined || unseen === 0 || newestId === seenUpTo.current) return;
    seenUpTo.current = newestId;
    markSeen(newestId);
  }, [newestId, unseen, markSeen]);
};

export const useFilterChoices = (unread: number) => {
  const { t } = usePageContext();
  return NOTIFICATION_FILTERS.map((filter) => ({
    value: filter,
    label: t(`notifications.filter_${filter}`),
    ...(filter === 'unread' ? { count: unread } : {}),
  }));
};

export const MarkAllRead = ({ api, newestId }: { api: TypedClient; newestId: string | undefined }) => {
  const { t } = usePageContext();
  const markRead = useMarkRead(api);
  return newestId === undefined ? null : (
    <Button
      variant="ghost"
      size="sm"
      icon={<CheckCheck size={16} aria-hidden="true" />}
      disabled={markRead.isPending}
      onClick={() => {
        markRead.mutate({ kind: 'all', upTo: newestId });
      }}
    >
      {t('notifications.markAllRead')}
    </Button>
  );
};

export const NotificationsPanel = ({ api, counts, onClose }: NotificationsPanelProps) => {
  const { t, locale, describeError } = usePageContext();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<NotificationFilter>('all');
  const pages = useNotificationPages(api, filter, PANEL_PAGE_SIZE);
  const choose = useChooseNotification(api, onClose);
  const choices = useFilterChoices(counts.unread);
  useSeeOnOpen(api, pages.items[0]?.id, counts.unseen);

  return (
    <Dialog
      open
      variant="popover"
      title={t('notifications.title')}
      subtitle={
        <div className={styles['toolbar']}>
          <ChoiceChips
            legend={t('notifications.filterLegend')}
            hideLegend
            choices={choices}
            value={filter}
            onChange={setFilter}
            locale={locale}
          />
          {counts.unread > 0 ? <MarkAllRead api={api} newestId={pages.items[0]?.id} /> : null}
        </div>
      }
      closeLabel={t('common.close')}
      onClose={onClose}
      footer={
        <Button
          variant="ghost"
          onClick={() => {
            onClose();
            void navigate({ to: '/notifications', search: { filter } });
          }}
        >
          {t('notifications.seeAll')}
        </Button>
      }
    >
      <NotificationList
        filter={filter}
        loading={pages.query.isPending}
        error={pages.query.isError ? describeError(pages.query.error) : undefined}
        onRetry={() => void pages.query.refetch()}
        items={pages.items}
        onChoose={choose}
      />
      {pages.hasMore ? (
        <Button variant="outline" disabled={pages.loadingMore} onClick={pages.loadMore}>
          {pages.loadingMore ? t('common.loadingMore') : t('notifications.seePrevious')}
        </Button>
      ) : null}
    </Dialog>
  );
};
